import { Flame, Repeat, Snowflake } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const columns = [
  {
    icon: Snowflake,
    accent: "text-ice",
    border: "border-ice/30",
    title: "The ice bath",
    tagline: "3–5°C of clarity",
    points: [
      "Eases post-exercise muscle soreness and helps take the edge off swelling and inflammation",
      "Triggers a rush of alertness — many regulars describe an immediate lift in mood and focus",
      "Studies link cold immersion with lower perceived stress in the hours after a plunge",
      "Every session builds mental resilience: learning to stay calm when everything says get out",
    ],
  },
  {
    icon: Flame,
    accent: "text-ember",
    border: "border-rust/40",
    title: "The traditional sauna",
    tagline: "Deep, dry Finnish heat",
    points: [
      "Regular sauna use is associated in long-term studies with better cardiovascular health markers",
      "Heat opens blood vessels and boosts circulation — a passive workout for your heart",
      "Deep relaxation: sauna bathing is linked with reduced anxiety and improved wellbeing",
      "Many bathers report deeper, easier sleep on sauna days",
    ],
  },
  {
    icon: Repeat,
    accent: "text-cream",
    border: "border-line",
    title: "Contrast therapy",
    tagline: "The two together",
    points: [
      "Alternating heat and cold works your blood vessels like a pump — dilate, constrict, repeat",
      "That vascular cycle helps move metabolic waste out and fresh, oxygenated blood in",
      "Research shows contrast therapy reduces muscle soreness and strength loss versus simply resting",
      "The finisher feeling: calm, light and reset — the reason people come back weekly",
    ],
  },
];

const Benefits = () => {
  return (
    <section id="benefits" data-testid="benefits-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <SectionHeading
            overline="Why it works"
            title={
              <>
                Cold does one thing. Heat does another.{" "}
                <em className="italic text-ember">Together they do more.</em>
              </>
            }
            className="lg:col-span-8"
          />
          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="text-base font-light leading-relaxed text-sand">
              Each half of the circuit earns its place on its own. Run them back to
              back and you get something neither can do alone.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-6">
          {columns.map((col, i) => (
            <Reveal key={col.title} delay={i * 0.1}>
              <article
                data-testid={`benefits-card-${i + 1}`}
                className={`h-full border ${col.border} bg-coal p-7 sm:p-8 flex flex-col`}
              >
                <col.icon className={`h-6 w-6 ${col.accent}`} aria-hidden="true" />
                <h3 className="mt-5 font-display uppercase tracking-wide text-2xl text-cream">{col.title}</h3>
                <p className={`mt-1 text-xs tracking-[0.2em] uppercase ${col.accent}`}>{col.tagline}</p>
                <ul className="mt-6 space-y-4">
                  {col.points.map((p) => (
                    <li key={p} className="flex gap-3 text-sm font-light leading-relaxed text-sand">
                      <span className={`mt-2 h-1 w-1 shrink-0 rounded-full bg-current ${col.accent}`} aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 text-xs font-light leading-relaxed text-stone2 max-w-2xl">
            Contrast therapy is a wellness practice, not a medical treatment. Findings above
            reflect published research on sauna, cold immersion and contrast bathing; individual
            results vary. Speak to your GP if you have a health condition.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Benefits;
